import classNames from 'classnames';
import React, { useState } from 'react';
import {
  NotebookIcons,
  StarIcons,
  VideoIcons,
} from '../../../../../../../../icons/icons';
import { ClockIcon } from '../../../../../../../../ui/icons/icons';
import { getSkillAssessmentVideo } from '../../../../../../../../utils/api_service/assessments_api';
import { getVideoFromStorage } from '../../../../../../../../utils/api_service/storage_api';
import { IQuestionAndAnswer } from '../Interfaces';

export const QuestionAndAnswer = (props: IQuestionAndAnswer) => {
  const {
    handleOpenQuestion,
    question,
    videoResponse,
    score,
    timeTaken,
    skillAttribute,
    openQuestion,
    response,
    followUpQuestions,
    systemAnswer,
    questionId,
    status,
    onStatusChange,
    isSkillAssessment,
  } = props;

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Function to fetch video from storage
  const fetchVideo = async (videoId: string) => {
    if (!videoId) return;

    setVideoLoading(true);
    setVideoError(null);

    try {
      let url: string;
      if (isSkillAssessment && typeof videoId === 'string') {
        // For skill assessment questions
        url = await getSkillAssessmentVideo(videoId);
      } else {
        // For BEI videos
        url = await getVideoFromStorage(videoId);
      }
      setVideoUrl(url);
    } catch (error) {
      setVideoError('Failed to load video');
    } finally {
      setVideoLoading(false);
    }
  };

  // Handle opening video modal
  const handleOpenVideoModal = () => {
    setIsVideoModalOpen(true);
    if (typeof videoResponse === 'string') {
      if (isSkillAssessment) {
        // For skill assessment questions, get video URL from the skills API
        fetchVideo(videoResponse);
      } else if (
        videoResponse.includes('/') ||
        videoResponse.includes('.webm') ||
        videoResponse.includes('.mp4')
      ) {
        // If videoResponse looks like a file path for BEI, fetch from storage
        fetchVideo(videoResponse);
      } else {
        // If it's already a URL, use it directly
        setVideoUrl(videoResponse);
      }
    } else {
      setVideoUrl(null);
      setVideoError('No video available');
    }
  };

  // Handle closing video modal
  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setVideoUrl(null);
    setVideoError(null);
    setVideoLoading(false);
  };

  // Video Modal Component
  const VideoModal = () => {
    if (!isVideoModalOpen) return null;

    return (
      <div className='fixed inset-0 z-50 overflow-y-auto'>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          { /* Background overlay */ }
          <div
              className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity'
              onClick={handleCloseVideoModal}
          ></div>

          { /* Modal panel */ }
          <div className='inline-block align-bottom bg-white rounded-none px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6 border border-gray-300'>
            <div className='absolute top-0 right-0 pt-4 pr-4'>
              <button
                  type='button'
                  className='bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  onClick={handleCloseVideoModal}
              >
                <span className='sr-only'>Close</span>
                <svg
                    className='h-6 w-6'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='sm:flex sm:items-start'>
              <div className='w-full'>
                { /* Modal Header */ }
                <div className='flex items-center gap-3 mb-6'>
                  <div className='w-8 h-8 rounded-none bg-blue-50 border border-blue-100 flex items-center justify-center'>
                    <VideoIcons className='w-3 h-3 text-blue-600' />
                  </div>
                  <div>
                    <h3 className='text-sm font-medium text-gray-900'>
                      Video Response
                    </h3>
                    <p className='text-sm text-gray-500 mt-1'>{ question }</p>
                  </div>
                </div>

                <div className='sm:grid sm:grid-cols-5 sm:gap-6'>
                  { /* Left Column: Video */ }
                  <div className='sm:col-span-3'>
                    { /* Video Player Container */ }
                    <div className='bg-gray-900 rounded-none overflow-hidden mb-4 sm:mb-0'>
                      <div className='aspect-video flex items-center justify-center h-full'>
                        { videoLoading ? (
                          <div className='flex flex-col items-center justify-center text-gray-400'>
                            <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-4'></div>
                            <p className='text-lg font-medium'>
                              Loading Video...
                            </p>
                            <p className='text-sm'>
                              Please wait while we fetch your video.
                            </p>
                          </div>
                        ) : videoError ? (
                          <div className='flex flex-col items-center justify-center text-gray-400'>
                            <VideoIcons className='w-16 h-16 mb-4 text-red-400' />
                            <p className='text-lg font-medium text-red-400'>
                              Error Loading Video
                            </p>
                            <p className='text-sm'>{ videoError }</p>
                          </div>
                        ) : videoUrl ? (
                          <video
                              controls
                              className='w-full h-full object-cover'
                              src={videoUrl}
                              preload='metadata'
                          >
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <div className='flex flex-col items-center justify-center text-gray-400'>
                            <VideoIcons className='w-16 h-16 mb-4' />
                            <p className='text-lg font-medium'>
                              Video Not Available
                            </p>
                            <p className='text-sm'>
                              The video response could not be loaded.
                            </p>
                          </div>
                        ) }
                      </div>
                    </div>
                  </div>

                  { /* Right Column: Metadata & Transcript */ }
                  <div className='sm:col-span-2 flex flex-col space-y-4'>
                    { /* Video Metadata */ }
                    <div className='grid grid-cols-1 gap-3'>
                      <div className='bg-gray-50 rounded-none border border-gray-200 p-3'>
                        <div className='flex items-center gap-2 mb-1'>
                          <div className='w-2 h-2 rounded-none bg-blue-500'></div>
                          <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                            Score
                          </span>
                        </div>
                        <div
                            className={`text-lg font-bold ${
                              score >= 85
                                ? 'text-green-600'
                                : score >= 70
                                  ? 'text-blue-600'
                                  : 'text-orange-600'
                            }`}
                        >
                          { score }%
                        </div>
                      </div>

                      <div className='bg-gray-50 rounded-none border border-gray-200 p-3'>
                        <div className='flex items-center gap-2 mb-1'>
                          <ClockIcon className='w-3 h-3 text-gray-400' />
                          <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                            Duration
                          </span>
                        </div>
                        <div className='text-lg font-bold text-gray-900'>
                          { timeTaken }
                        </div>
                      </div>

                      <div className='bg-gray-50 rounded-none border border-gray-200 p-3'>
                        <div className='flex items-center gap-2 mb-1'>
                          <div className='w-2 h-2 rounded-none bg-purple-500'></div>
                          <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                            Skill
                          </span>
                        </div>
                        <div className='text-[13px] font-bold text-gray-800 truncate uppercase tracking-tight'>
                          { skillAttribute }
                        </div>
                      </div>
                    </div>

                    { /* Response Text (if available) */ }
                    { response && (
                      <div className='bg-gray-50 rounded-none border border-gray-200 p-4 flex-1 flex flex-col min-h-0'>
                        <h4 className='text-xs font-bold text-gray-600 uppercase tracking-wider mb-2'>
                          Transcript
                        </h4>
                        <div className='overflow-y-auto max-h-[150px] sm:max-h-[250px] pr-2 custom-scrollbar'>
                          <p className='text-sm text-gray-700 leading-relaxed whitespace-pre-wrap'>
                            { response }
                          </p>
                        </div>
                      </div>
                    ) }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <VideoModal />
      <div
          className={classNames(
            'border-b border-gray-100 last:border-none transition-all duration-500',
            openQuestion === question
              ? 'bg-emerald-50 shadow-[inset_0_2px_10px_rgba(0,0,0,0.02)]'
              : 'bg-white',
          )}
      >
        <div className='px-6 py-4'>
          <button
              onClick={() => handleOpenQuestion(question)}
              className='w-full flex items-start justify-between gap-4 text-left'
          >
            <div className='flex-1'>
              <p className='text-sm text-gray-900'>{ question }</p>
              { videoResponse && (
                <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenVideoModal();
                    }}
                    className='inline-flex items-center px-2 py-0.5 mt-2 rounded-none text-[9px] font-bold uppercase tracking-widest bg-blue-50/50 text-blue-500 border border-blue-100 hover:bg-blue-100/50 transition-all duration-300 cursor-pointer'
                >
                  <VideoIcons className='w-2.5 h-2.5 mr-1.5' />
                  Video Response
                </button>
              ) }
            </div>
            <div className='flex items-center justify-end gap-6 min-w-[400px]'>
              <div className='flex items-center gap-2 text-xs font-medium text-blue-600 group-hover:text-blue-700 transition-colors'>
                <span>
                  { openQuestion === question
                    ? 'Hide transcript'
                    : 'Click to see transcript' }
                </span>
                <svg
                    className={`w-4 h-4 transition-transform duration-200 ${
                      openQuestion === question ? 'rotate-180' : ''
                    }`}
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 9l-7 7-7-7'
                  />
                </svg>
              </div>
              <div className='flex items-center gap-2 px-2 py-0.5 bg-white border border-gray-100 shadow-sm rounded-none'>
                <div className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>
                  { skillAttribute }
                </div>
              </div>
            </div>
          </button>

          { openQuestion === question && (
            <div className='mt-6'>
              { /* Answer Section */ }
              { response && (
                <div className='grid grid-cols-1 gap-6'>
                  { /* System Answer */ }

                  { /* Candidate Response */ }

                  <div className='bg-white rounded-none p-5 border border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]'>
                    <div className='flex items-center gap-2.5 mb-4'>
                      { videoResponse ? (
                        <div className='w-5 h-5 rounded-none bg-blue-50/50 border border-blue-100/50 flex items-center justify-center'>
                          <VideoIcons className='w-2.5 h-2.5 text-blue-500' />
                        </div>
                      ) : (
                        <div className='w-5 h-5 rounded-none bg-blue-50/50 border border-blue-100/50 flex items-center justify-center'>
                          <span className='text-[9px] font-bold text-blue-500'>
                            T
                          </span>
                        </div>
                      ) }
                      <h3 className='text-[11px] font-bold text-gray-400 uppercase tracking-widest'>
                        Response Transcript
                      </h3>
                    </div>
                    <p className='text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap'>
                      { response }
                    </p>
                  </div>
                </div>
              ) }

              { /* STAR Breakdown & Smart Chart */ }

              { /* Follow-up Questions */ }
              { followUpQuestions && followUpQuestions.length > 0 && (
                <div className='mt-6 '>
                  <div className='flex items-center gap-2 mb-4'>
                    <div className='w-5 h-5 rounded-none bg-purple-100 flex items-center justify-center'>
                      <span className='text-xs font-medium text-purple-600'>
                        <NotebookIcons />
                      </span>
                    </div>
                    <h4 className='text-sm font-medium text-gray-900'>
                      Response Analysis
                    </h4>
                  </div>
                  <div className='space-y-4'>
                    { followUpQuestions.map((fq, idx) => (
                      <div
                          key={idx}
                          className='bg-white rounded-none p-4 border border-gray-100 hover:shadow-md transition-all duration-300'
                      >
                        <p className='text-[13px] text-gray-800 mb-2 leading-relaxed'>
                          { fq }
                        </p>
                        <div className='text-[9px] font-bold text-gray-400 uppercase tracking-widest opacity-60'>
                          { idx === 0 &&
                            'Probes situational awareness and problem recognition' }
                          { idx === 1 &&
                            'Assesses emotional regulation and initial response mechanisms' }
                          { idx === 2 &&
                            'Evaluates critical thinking and decision-making process' }
                        </div>
                      </div>
                    )) }
                  </div>
                </div>
              ) }
            </div>
          ) }
        </div>
      </div>
    </>
  );
};
